import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Typography } from '@progress/kendo-react-common';
import { TreeList, TreeListCellProps, TreeListExpandChangeEvent } from '@progress/kendo-react-treelist';
import { ItemNodeFilesDetails } from '../../types';
import { Icon } from '../../components/layout';
import { formatSizeUnits } from '../../converters/formatSizeUnits';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from '../../hooks/authentication';

interface ProjectFileInfo {
  id: string;
  text: string;
  size: string;
  expanded?: boolean;
  isLoading?: boolean;
  inputFileData: ItemNodeFilesDetails[];
  outputFileData: ItemNodeFilesDetails[];
}

const StyledTreeList = styled(TreeList)`
  // Font size
  font-size: 0.9rem;

  // Border resetting
  th,
  td,
  tr[aria-level='1']:first-child > td {
    border: none;
  }
  th {
    border-bottom: 1px solid var(--geosap-primary-color);
  }
  tr[aria-level='1'] > td {
    border-top: 1px solid var(--geosap-primary-color);
  }

  // Border radius
  border-radius: 0.5rem;
  table {
    border-radius: 0.5rem;
  }

  // Background color reset
  .k-grid-header {
    background-color: unset;
  }
`;

const projectFiles: ProjectFileInfo[] = [
  {
    id: 'Uploads',
    text: 'Uploads',
    size: '0MB',
    inputFileData: [
      {
        id: uuidv4(),
        transactionId: uuidv4(),
        name: 'Point Clouds',
        size: 109940440,
      },
      {
        id: uuidv4(),
        transactionId: uuidv4(),
        name: 'Imagery',
        size: 64940440,
      },
    ],
    outputFileData: [
      {
        id: uuidv4(),
        transactionId: uuidv4(),
        name: 'Point Clouds',
        size: 53240440,
      },
      {
        id: uuidv4(),
        transactionId: uuidv4(),
        name: 'Imagery',
        size: 12040440,
      },
    ],
  },
];

const ActionsCell: React.FC<TreeListCellProps> = ({dataItem}) => {
  const { getUser } = useUser();
  if(dataItem.id === 'Uploads' || dataItem.id === 'Outputs') {
    return (<td></td>);
  }

  const downloadUrl = process.env.REACT_APP_GEOSAP_SERVERURL +
    '/file_manager/download_items?user_id=' +
    getUser().id +
    '&transaction_id=' +
    dataItem.transactionId +
    '&item_id_list=' +
    dataItem.id + 
    '&filename=' + 
    dataItem.text + '.zip';
  return (
    <td>
      <span className="pe-2">
        <a href={downloadUrl}><Icon iconClass="k-i-download" size="1.1rem" color="green" /></a>
      </span>
    </td>
  );
};

interface Props {
  isLoading?: boolean;
  isSuccess?: boolean;
  inputFileData: ItemNodeFilesDetails[];
  outputFileData: ItemNodeFilesDetails[];
}

const ProjectFilesSection: React.FC<Props> = ({ isLoading, isSuccess, inputFileData, outputFileData }) => {
  const [treeListData, setTreeListData] = useState(projectFiles);

  useEffect(() => {
    if (isSuccess) {
      const newTreeData: any = [
        {
          id: 'Uploads',
          text: 'Uploads',
          size: formatSizeUnits(
            inputFileData.reduce((accumulator, currentValue) => accumulator + currentValue.size, 0)
          ),
          files: inputFileData.map((fileData) => {
            return { id: fileData.id, transactionId: fileData.transactionId, text: fileData.name, size: formatSizeUnits(fileData.size) };
          }),
        },
        {
          id: 'Outputs',
          text: 'Outputs',
          size: formatSizeUnits(
            outputFileData.reduce((accumulator, currentValue) => accumulator + currentValue.size, 0)
          ),
          files: outputFileData.map((fileData) => {
            return { id: fileData.id, transactionId: fileData.transactionId, text: fileData.name, size: formatSizeUnits(fileData.size) };
          }),
        },
      ];
      setTreeListData(newTreeData);
    }
  }, [isSuccess, inputFileData, outputFileData]);

  const handleExpandChange = (e: TreeListExpandChangeEvent): void => {
    setTreeListData((prevState) =>
      prevState.map((file) => ({
        ...file,
        expanded: e.dataItem.id === file.id ? !file.expanded : file.expanded,
      }))
    );
  };

  return (
    <div>
      <Typography.h4 fontWeight="normal" className="text-uppercase">
        Project Files
      </Typography.h4>
      <StyledTreeList
        className="mt-3"
        data={treeListData}
        expandField="expanded"
        subItemsField="files"
        columns={[
          { field: 'text', title: 'Name', expandable: true, width: '200px', className: "mw-200" },
          { field: 'size', title: 'Size', width: '50px' },
          { title: 'Actions', cell: ActionsCell, width: '50px' },
        ]}
        onExpandChange={handleExpandChange}
      />
    </div>
  );
};

export default ProjectFilesSection;
